import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { getApiBaseUrl } from '../api/config';
import { resolveShopTheme, themeCssVars, type ShopBranding, type ShopTheme } from '../theme';
import { useI18n } from '../i18n';
import { formatMoney } from '../i18n/format';
import { AccentButton, ShopFooter, SmallCaps } from '../components/ShopChrome';
import { Field } from '../components/FormField';
import { inputStyle } from '../components/formStyles';
import type { ShopLegal } from '../legal';

/** Only the fields this page reads off the catalogue. */
interface ShopProduct {
  id: string;
  name: string;
  amount: number;
  currency?: string;
}

interface ShopPackage {
  id: string;
  name: string;
  final_amount: number;
  currency?: string;
}

interface ShopData {
  location_id: string;
  products: ShopProduct[];
  packages: ShopPackage[];
  location_name?: string;
  location_address?: string;
  location_language?: string;
  branding?: ShopBranding;
  legal?: ShopLegal;
  currency?: string;
}

interface PurchaseItem {
  id: string;
  type: 'product' | 'package' | 'custom';
  name: string;
  amount: number;
  currency: string;
}

type DeliveryMethod = 'email' | 'print' | 'self';

interface PurchaseFormData {
  recipientFirstName: string;
  recipientName: string;
  recipientEmail: string;
  buyerName: string;
  buyerEmail: string;
  message: string;
  deliveryMethod: DeliveryMethod;
}

const DELIVERY_OPTIONS: DeliveryMethod[] = ['email', 'self', 'print'];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PurchasePage: React.FC = () => {
  const { locationId, itemId } = useParams<{ locationId: string; itemId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const type = searchParams.get('type') || 'product';
  const amountParam = searchParams.get('amount');
  const { t, locale, applyVenueLocale } = useI18n();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shop, setShop] = useState<ShopData | null>(null);
  const [item, setItem] = useState<PurchaseItem | null>(null);
  const [formData, setFormData] = useState<PurchaseFormData>({
    recipientFirstName: '',
    recipientName: '',
    recipientEmail: '',
    buyerName: '',
    buyerEmail: '',
    message: '',
    deliveryMethod: 'email',
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!locationId) {
        setError('Location ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${getApiBaseUrl()}/gift-vouchers/shop/${locationId}`);
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          setError(errorData.error?.message || t('purchase.error_load'));
          setLoading(false);
          return;
        }

        const data: ShopData = await response.json();
        setShop(data);
        applyVenueLocale(data.location_language);

        const fallbackCurrency = data.currency || data.products[0]?.currency || 'EUR';

        if (itemId === 'custom') {
          const amount = parseFloat(amountParam || '');
          if (!amount || Number.isNaN(amount)) {
            setError(t('purchase.error_no_amount'));
          } else {
            setItem({
              id: 'custom',
              type: 'custom',
              name: t('order.gift_voucher'),
              amount,
              currency: searchParams.get('currency') || fallbackCurrency,
            });
          }
        } else if (type === 'package') {
          const pkg = data.packages.find((p) => p.id === itemId);
          if (pkg) {
            setItem({
              id: pkg.id,
              type: 'package',
              name: pkg.name,
              amount: pkg.final_amount,
              currency: pkg.currency || fallbackCurrency,
            });
          } else {
            setError(t('purchase.error_package_gone'));
          }
        } else {
          const product = data.products.find((p) => p.id === itemId);
          if (product) {
            setItem({
              id: product.id,
              type: 'product',
              name: product.name,
              amount: product.amount,
              currency: product.currency || fallbackCurrency,
            });
          } else {
            setError(t('purchase.error_product_gone'));
          }
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(t('purchase.error_load'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [locationId, itemId, type, amountParam, searchParams, t, applyVenueLocale]);

  const theme = useMemo(() => resolveShopTheme(shop?.branding), [shop?.branding]);

  const setField = (field: keyof PurchaseFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const validate = (): string | null => {
    if (!formData.recipientFirstName.trim() || !formData.recipientName.trim()) {
      return t('purchase.error_recipient_name');
    }
    if (!EMAIL_PATTERN.test(formData.recipientEmail.trim())) {
      return t('purchase.error_recipient_email');
    }
    if (!formData.buyerName.trim()) {
      return t('purchase.error_your_name');
    }
    if (!EMAIL_PATTERN.test(formData.buyerEmail.trim())) {
      return t('purchase.error_your_email');
    }
    return null;
  };

  const handleContinue = () => {
    if (!item || !locationId) return;
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }

    sessionStorage.setItem(
      'checkoutData',
      JSON.stringify({
        itemId: item.id,
        type: item.type,
        amount: item.amount,
        currency: item.currency,
        itemName: item.name,
        recipientName: formData.recipientName.trim(),
        recipientFirstName: formData.recipientFirstName.trim(),
        recipientEmail: formData.recipientEmail.trim(),
        personalMessage: formData.message.trim(),
        deliveryMethod: formData.deliveryMethod === 'self' ? 'myself' : formData.deliveryMethod,
        buyerName: formData.buyerName.trim(),
        buyerEmail: formData.buyerEmail.trim(),
        locationId,
      })
    );
    navigate('/checkout');
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={themeCssVars(theme)}>
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: theme.accent }} />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="flex min-h-screen items-center justify-center px-5" style={themeCssVars(theme)}>
        <div className="max-w-md text-center">
          <h1 style={{ font: `500 26px/1.2 ${theme.fontFamily}`, color: theme.ink, margin: '0 0 8px' }}>
            {t('purchase.error_title')}
          </h1>
          <p style={{ font: `400 15px/1.6 ${theme.fontFamily}`, color: theme.muted, margin: '0 0 24px' }}>
            {error || t('purchase.error_body')}
          </p>
          <AccentButton theme={theme} onClick={() => navigate(`/shop/${locationId}`)}>
            {t('purchase.back_to_shop')}
          </AccentButton>
        </div>
      </div>
    );
  }

  const total = formatMoney(item.amount, item.currency, locale);

  return (
    <div className="flex min-h-screen flex-col" style={themeCssVars(theme)}>
      <header
        className="flex items-center justify-between gap-4 px-5 py-4 sm:px-10 sm:py-[22px]"
        style={{ borderBottom: `1px solid ${theme.hairline}` }}
      >
        <button
          type="button"
          onClick={() => navigate(`/shop/${locationId}`)}
          className="inline-flex items-center gap-2"
          style={{ font: `500 14px/1 ${theme.fontFamily}`, color: theme.body }}
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common.back')}
        </button>
        <SmallCaps theme={theme}>{t('purchase.step')}</SmallCaps>
      </header>

      <main className="grid flex-1 gap-10 px-5 py-10 sm:px-10 sm:py-14 lg:grid-cols-[1.35fr_.65fr] lg:gap-12">
        <div>
          <div className="mb-8 flex gap-2">
            <div className="h-0.5 flex-1" style={{ background: theme.ink }} />
            <div className="h-0.5 flex-1" style={{ background: theme.inputBorder }} />
          </div>

          <h1
            style={{
              margin: '0 0 8px',
              font: `500 clamp(26px, 4.5vw, 34px)/1.15 ${theme.fontFamily}`,
              letterSpacing: '-.02em',
              color: theme.ink,
            }}
          >
            {t('purchase.title')}
          </h1>
          <p style={{ margin: '0 0 34px', font: `400 15px/1.6 ${theme.fontFamily}`, color: theme.muted }}>
            {t('purchase.subtitle')}
          </p>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field theme={theme} label={t('purchase.first_name')}>
              <input
                type="text"
                value={formData.recipientFirstName}
                onChange={(e) => setField('recipientFirstName', e.target.value)}
                style={inputStyle(theme)}
                autoFocus
              />
            </Field>
            <Field theme={theme} label={t('purchase.last_name')}>
              <input
                type="text"
                value={formData.recipientName}
                onChange={(e) => setField('recipientName', e.target.value)}
                style={inputStyle(theme)}
              />
            </Field>
            <Field theme={theme} label={t('purchase.their_email')} className="sm:col-span-2">
              <input
                type="email"
                value={formData.recipientEmail}
                onChange={(e) => setField('recipientEmail', e.target.value)}
                style={inputStyle(theme)}
              />
            </Field>

            <div className="h-px sm:col-span-2" style={{ background: theme.hairline }} />

            <Field theme={theme} label={t('purchase.your_name')}>
              <input
                type="text"
                value={formData.buyerName}
                onChange={(e) => setField('buyerName', e.target.value)}
                style={inputStyle(theme)}
              />
            </Field>
            <Field theme={theme} label={t('purchase.your_email')}>
              <input
                type="email"
                value={formData.buyerEmail}
                onChange={(e) => setField('buyerEmail', e.target.value)}
                style={inputStyle(theme)}
              />
            </Field>

            <Field
              theme={theme}
              label={t('purchase.message')}
              hint={t('purchase.message_hint')}
              className="sm:col-span-2"
            >
              <textarea
                rows={3}
                maxLength={500}
                value={formData.message}
                onChange={(e) => setField('message', e.target.value)}
                style={{ ...inputStyle(theme), resize: 'none', lineHeight: 1.55 }}
              />
            </Field>

            <Field theme={theme} label={t('purchase.delivery')} className="sm:col-span-2">
              <div className="grid gap-2 sm:grid-cols-3">
                {DELIVERY_OPTIONS.map((option) => {
                  const active = formData.deliveryMethod === option;
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setField('deliveryMethod', option)}
                      className="px-4 py-3 text-left"
                      style={{
                        background: theme.surface,
                        border: `1px solid ${active ? theme.ink : theme.inputBorder}`,
                        borderRadius: theme.radius,
                      }}
                    >
                      <div style={{ font: `500 14px/1.3 ${theme.fontFamily}`, color: theme.ink }}>
                        {t(`purchase.delivery_${option}`)}
                      </div>
                      <div className="mt-1" style={{ font: `400 12px/1.4 ${theme.fontFamily}`, color: theme.label }}>
                        {t(`purchase.delivery_${option}_hint`)}
                      </div>
                    </button>
                  );
                })}
              </div>
            </Field>
          </div>

          {error && (
            <p className="mt-6" style={{ font: `400 14px/1.5 ${theme.fontFamily}`, color: theme.accent }}>
              {error}
            </p>
          )}

          <div
            className="mt-8 flex flex-wrap items-center justify-between gap-4 pt-5"
            style={{ borderTop: `1px solid ${theme.hairline}` }}
          >
            <span style={{ font: `400 13px/1.4 ${theme.fontFamily}`, color: theme.label }}>{t('purchase.next')}</span>
            <AccentButton theme={theme} onClick={handleContinue} className="max-sm:w-full">
              {t('common.continue')} <ArrowRight className="h-4 w-4" />
            </AccentButton>
          </div>
        </div>

        {/* Order summary — in view throughout, so nothing is a surprise at payment */}
        <aside
          className="h-fit p-6 lg:sticky lg:top-8"
          style={{ background: theme.surface, border: `1px solid ${theme.hairline}`, borderRadius: theme.radius }}
        >
          <SmallCaps theme={theme}>{t('order.heading')}</SmallCaps>
          <div className="mt-5 flex flex-col gap-3">
            <Row theme={theme} label={item.name} value={total} />
            <Row
              theme={theme}
              label={t('order.delivery')}
              value={t(`order.delivery_${formData.deliveryMethod}`)}
            />
            <div className="h-px" style={{ background: theme.hairlineSoft }} />
            <div className="flex items-baseline justify-between">
              <span style={{ font: `500 15px/1 ${theme.fontFamily}`, color: theme.ink }}>{t('order.total')}</span>
              <span
                style={{
                  font: `500 26px/1 ${theme.fontFamily}`,
                  color: theme.ink,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {total}
              </span>
            </div>
          </div>
          <p
            className="mt-6 pt-5"
            style={{
              borderTop: `1px solid ${theme.hairlineSoft}`,
              font: `400 13px/1.6 ${theme.fontFamily}`,
              color: theme.label,
              margin: 0,
            }}
          >
            {t('order.note_validity')}
          </p>
        </aside>
      </main>

      <ShopFooter
        theme={theme}
        venueName={shop?.location_name}
        venueAddress={shop?.location_address}
        legal={shop?.legal}
        locationId={locationId}
      />
    </div>
  );
};

const Row: React.FC<{ theme: ShopTheme; label: string; value: string }> = ({ theme, label, value }) => (
  <div className="flex justify-between gap-4" style={{ font: `400 14px/1.4 ${theme.fontFamily}`, color: theme.body }}>
    <span className="min-w-0">{label}</span>
    <span style={{ color: theme.ink, whiteSpace: 'nowrap' }}>{value}</span>
  </div>
);

export default PurchasePage;
